import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppInput from "../../components/Core/AppInput/AppInput";
import AppText from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Modal from "../../components/Core/Modal/Modal";
import Flex from "../../components/Layout/Flex";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import CorporateGroup, { CorporateGroupInvoiceDetails } from "../../models/CorporateGroup";
import {
	useCorporateGroup,
	useCreateCorporateGroup,
	useDeleteCorporateGroup,
	useUpdateCorporateGroup,
} from "../../networking/corporateGroup.hooks";
import { customerRoute } from "../../utils/route.utils";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldFormValues,
	customFieldsSchema,
	groupOwnedCustomFields,
} from "../../components/Shared/CustomFields";
import {
	DeliveryMethodRadioGroup,
	getDeliveryErrorTarget,
	getDeliveryMethodOptions,
	getEffectiveDeliveryMethod,
	getGLNError,
	usesDeliveryField,
} from "../../components/Shared/DeliveryMethod";
import { toast } from "../../utils/toast";

interface Props {
	/** When set the modal edits an existing corporate group, otherwise creates a new one */
	corporateGroupId?: string;
	onClose: () => void;
	onSaved?: (corporateGroup: CorporateGroup) => void;
}

type CorporateGroupInputs = {
	name: string;
	invoiceDetails: CorporateGroupInvoiceDetails;
	customFields?: CustomFieldFormValues;
};

/**
 * Create/edit modal for a corporate group (koncern). The group owns the
 * invoice details shared by all its member customers — editing here is the
 * "update in one place" surface. The same goes for the organisation's
 * group-owned customer custom fields ("Ägs av koncern"), rendered in
 * definition order below the invoice details.
 */
const CorporateGroupFormModal: React.FC<Props> = ({ corporateGroupId, onClose, onSaved }) => {
	const { t } = useTranslation();

	const isEdit = !!corporateGroupId;
	const { corporateGroup } = useCorporateGroup(corporateGroupId);

	const createCorporateGroup = useCreateCorporateGroup();
	const updateCorporateGroup = useUpdateCorporateGroup();
	const deleteCorporateGroup = useDeleteCorporateGroup();

	const { data: organisation } = useOrganisation();
	const exportType = organisation?.invoiceSettings?.exportType;

	const customerFieldDefinitions = useCustomFieldDefinitions("customer");
	const customFieldDefinitions = React.useMemo(
		() => groupOwnedCustomFields(customerFieldDefinitions),
		[customerFieldDefinitions]
	);

	const { register, handleSubmit, formState, reset, watch, setValue, control, setError } =
		useForm<CorporateGroupInputs>({
			defaultValues: { name: "", invoiceDetails: { deliveryMethod: "PRINT" }, customFields: {} },
		});

	React.useEffect(() => {
		if (corporateGroup)
			reset({
				name: corporateGroup.name,
				invoiceDetails: {
					...corporateGroup.invoiceDetails,
					deliveryMethod: getEffectiveDeliveryMethod(corporateGroup.invoiceDetails),
				},
				customFields: customFieldFormValues(customFieldDefinitions, corporateGroup.customFields),
			});
		// Create: seed the controls (booleans must start controlled) once the definitions load.
		else if (!isEdit)
			setValue("customFields", customFieldFormValues(customFieldDefinitions, undefined));
	}, [corporateGroup, reset, setValue, isEdit, customFieldDefinitions]);

	const members = corporateGroup?.members || [];
	// Members whose method Fortnox owns keep their own value. Counted only when
	// the backend includes the member's invoiceDetails in the group response.
	const managedMemberCount = members.filter(
		(member) => member.invoiceDetails?.deliveryManagedBy
	).length;

	const deliveryMethod = watch("invoiceDetails.deliveryMethod") ?? "PRINT";
	const savedDeliveryMethod = corporateGroup
		? getEffectiveDeliveryMethod(corporateGroup.invoiceDetails)
		: undefined;

	const onSubmit = handleSubmit(async (e) => {
		// The form has no resolver (inline register rules), so the custom fields'
		// required check runs here with the shared yup schema.
		if (customFieldDefinitions.length > 0) {
			try {
				await customFieldsSchema(customFieldDefinitions).validate(e.customFields ?? {}, {
					abortEarly: false,
				});
			} catch (validationError) {
				const inner = (validationError as { inner?: { path?: string; message: string }[] }).inner;
				inner?.forEach(({ path, message }) => {
					if (path) setError(`customFields.${path}` as const, { message });
				});
				return;
			}
		}

		const body = {
			name: e.name,
			invoiceDetails: {
				address1: e.invoiceDetails?.address1,
				address2: e.invoiceDetails?.address2,
				postalCode: e.invoiceDetails?.postalCode,
				city: e.invoiceDetails?.city,
				email: e.invoiceDetails?.email || undefined,
				GLNCode: e.invoiceDetails?.GLNCode,
				deliveryMethod: e.invoiceDetails?.deliveryMethod,
			},
			customFields: buildCustomFieldsPayload(e.customFields, customFieldDefinitions),
		};

		try {
			const saved = isEdit
				? await updateCorporateGroup.mutateAsync({
						corporateGroupId: corporateGroupId as string,
						update: body,
				  })
				: await createCorporateGroup.mutateAsync(body);

			toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
			onSaved?.(saved);
			onClose();
		} catch (err) {
			const target = getDeliveryErrorTarget(
				(err as { error?: { code?: string; detail?: string } })?.error
			);

			if (target) toast.error(t(target.message as any));
			else toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	});

	const onDelete = async () => {
		if (!corporateGroupId || members.length > 0) return;

		try {
			await deleteCorporateGroup.mutateAsync(corporateGroupId);
			toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
			onClose();
		} catch (err) {
			toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	};

	return (
		<Modal maxWidth="540px" padding="2.5rem" centerContent={false} onClose={onClose}>
			<AppText size="xm" weight="bold" margin="0 0 1rem 0">
				{t(isEdit ? "corporateGroup.edit" : "corporateGroup.createNew")}
			</AppText>

			<form onSubmit={onSubmit}>
				<AppInput
					{...register("name", { required: true })}
					label="corporateGroup.name"
					margin="0.5rem 0"
					placeholder=""
					errors={formState.errors?.name && t("FIELD_IS_REQUIRED")}
					mandatory
				/>

				<AppInput
					{...register("invoiceDetails.address1")}
					label="INVOICE_ADDRESS"
					margin="0.5rem 0 0 0"
					placeholder="INVOICE_ADDRESS"
					containerMargin="0 0 0.5rem 0"
				/>
				<AppInput
					{...register("invoiceDetails.address2")}
					margin="0"
					placeholder="INVOICE_ADDRESS_LINE_2"
					containerMargin="0 0 0.5rem 0"
				/>
				<Flex margin="0 0 0.5rem 0">
					<AppInput
						{...register("invoiceDetails.postalCode")}
						placeholder="POSTAL_CODE_PLACEHOLDER"
						containerWidth="40%"
						containerMargin="0 0.5rem 0 0"
					/>
					<AppInput
						{...register("invoiceDetails.city")}
						placeholder="CITY_PLACEHOLDER"
						containerWidth="60%"
					/>
				</Flex>
				<DeliveryMethodRadioGroup
					containerClassName="mt-4 mb-2"
					value={deliveryMethod}
					onChange={(value) =>
						setValue("invoiceDetails.deliveryMethod", value, { shouldDirty: true })
					}
					options={getDeliveryMethodOptions({ exportType, current: savedDeliveryMethod })}
					exportType={exportType}
					note={
						managedMemberCount > 0
							? t("deliveryMethod.managedMembers", { count: managedMemberCount })
							: undefined
					}
				/>
				<AppInput
					{...register("invoiceDetails.email", {
						validate: (value, values) =>
							values.invoiceDetails?.deliveryMethod !== "EMAIL" || !!value?.trim(),
					})}
					label="EMAIL_INVOICE"
					margin="0.5rem 0"
					placeholder="EMAIL_INVOICE_PLACEHOLDER"
					// A group is never managed: under Fortnox both fields stay editable
					disabled={!usesDeliveryField(deliveryMethod, "email", { exportType })}
					mandatory={deliveryMethod === "EMAIL"}
					errors={formState.errors?.invoiceDetails?.email && t("deliveryMethod.field.email")}
				/>
				<AppInput
					{...register("invoiceDetails.GLNCode", {
						// 13 digits when set, for every method; required for E_INVOICE_GLN
						validate: (value, values) =>
							getGLNError(values.invoiceDetails?.deliveryMethod, value) ?? true,
					})}
					label="customer.gln"
					margin="0.5rem 0"
					placeholder="GLN_PLACEHOLDER"
					disabled={!usesDeliveryField(deliveryMethod, "GLNCode", { exportType })}
					mandatory={deliveryMethod === "E_INVOICE_GLN"}
					errors={
						formState.errors?.invoiceDetails?.GLNCode &&
						t(
							(formState.errors.invoiceDetails.GLNCode.message ||
								"deliveryMethod.field.GLNCode") as any
						)
					}
				/>

				{customFieldDefinitions.length > 0 && (
					<div className="mt-4">
						<AppText weight="bold">{t("corporateGroup.customFieldsTitle")}</AppText>
						<p className="mb-0 mt-1 text-xs text-gray-500">
							{t("corporateGroup.customFieldsHint")}
						</p>
						<CustomFieldsSection
							entity="customer"
							ignorePlacement
							variant="inline"
							className="mt-3"
							definitions={customFieldDefinitions}
							control={control}
							register={register}
							errors={formState.errors}
						/>
					</div>
				)}

				{isEdit && (
					<div className="mt-4">
						<AppText weight="bold">{t("corporateGroup.members")}</AppText>
						{members.length ? (
							<ul className="mt-1 mb-2 max-h-48 overflow-y-auto pr-2">
								{members.map((member) => (
									<li key={member.id}>
										<Link
											to={customerRoute(member.id)}
											className="text-blue-600 hover:underline"
											target="_blank"
										>
											{member.name} (#{member.customerNumber})
										</Link>
									</li>
								))}
							</ul>
						) : (
							<AppText margin="0.25rem 0 0.5rem 0">{t("corporateGroup.noMembers")}</AppText>
						)}
					</div>
				)}

				<Flex justifyContent="space-between" margin="1.5rem 0 0 0">
					<Flex gap="0.5rem">
						<Button
							type="submit"
							width="9.75rem"
							loading={createCorporateGroup.isLoading || updateCorporateGroup.isLoading}
						>
							{t(isEdit ? "SAVE_CHANGES" : "corporateGroup.create")}
						</Button>
						<button
							type="button"
							className="px-4 py-2 text-gray-600 hover:text-gray-900"
							onClick={onClose}
						>
							{t("CANCEL")}
						</button>
					</Flex>

					{isEdit && (
						<button
							type="button"
							className="shrink-0 whitespace-nowrap px-2 text-red-600 disabled:text-gray-400 disabled:cursor-not-allowed"
							disabled={members.length > 0}
							title={members.length > 0 ? t("corporateGroup.deleteBlocked") : undefined}
							onClick={onDelete}
						>
							{t("corporateGroup.delete")}
						</button>
					)}
				</Flex>
			</form>
		</Modal>
	);
};

export default CorporateGroupFormModal;
